import React from 'react';
import { useMobile } from '../../hooks/use-mobile';
import clockIcon from '../../images/icons/icon-clock.svg';
import alertIcon from '../../images/icons/icon-alert.svg';
import boxIcon from '../../images/icons/icon-box.svg';
import hourglassIcon from '../../images/icons/icon-hourglass.svg';
import {
  Box,
  Card as MuiCard,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  makeStyles,
  createStyles,
  Paper,
} from '@material-ui/core';
import { DeliveryTotals } from '../../hooks/freights/useGetFreightStatistics';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { checkAdmin } from '../../services/validation';
import {
  DriverStatus,
  GetDriverStatisticsResponse,
} from '../../hooks/driver/useGetDriverStatistics';

interface DriverData {
  driverId: string;
  username: string;
  email: string;
  cpf: string | null;
  rg: number | null;
  phoneNumber: string | null;
  birthDate: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface Props {
  loading: boolean;
  driverData?: GetDriverStatisticsResponse | undefined;
  data?: DeliveryTotals;
}

const CARD_ICON: { [key: string]: any } = {
  clock: clockIcon,
  alert: alertIcon,
  box: boxIcon,
  hourglass: hourglassIcon,
};

const useStyles = makeStyles(theme =>
  createStyles({
    card: {
      border: '2px solid #fff',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
    },
    title: {
      fontFamily: 'Quicksand, sans-serif',
      fontSize: '2rem',
      color: '#1c2434',
      lineHeight: '2rem',
      fontWeight: 500,
    },
    typography: {
      backgroundColor: '#FF7206',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
      padding: '10px',
      fontFamily: 'Quicksand, sans-serif',
      fontWeight: 500,
      color: '#fff',
      letterSpacing: '0.1rem',
    },
    paragraph: {
      fontFamily: 'Quicksand, sans-serif',
      fontWeight: 500,
      color: '#64748B',
      fontSize: '1rem',
      lineHeight: '1rem',

      overflow: 'initial',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  }),
);

const formatTimeFromSeconds = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

const Cards: React.FC<Props> = ({ data, loading, driverData }: Props) => {
  const isMobile = useMobile();
  const classes = useStyles();
  const token = useSelector(
    (state: ApplicationState) => state.auth.token || state.distributor.token,
  );

  return (
    <>
      {loading || (!data && !driverData) ? (
        <CircularProgress />
      ) : (
        <>
          {checkAdmin(token) && driverData ? (
            <>
              <Paper className={classes.typography}>
                <Typography>MOTORISTAS</Typography>
              </Paper>
              <Box
                display="flex"
                flexWrap="wrap"
                justifyContent="space-between"
                marginTop={2}
              >
                <Grid container spacing={3}>
                  <Card
                    title={'Total'}
                    total={
                      driverData?.driversTotals?.reduce(
                        (acc, driver) => acc + Number(driver.COUNT),
                        0,
                      ) ?? 0
                    }
                    isMobile={isMobile}
                  />
                  <Card
                    title={'Ativos'}
                    total={
                      driverData?.driversTotals.find(
                        driver => driver.STATUS === DriverStatus.ACTIVE,
                      )?.COUNT ?? 0
                    }
                    isMobile={isMobile}
                  />
                  <Card
                    title={'Aprovação pendente'}
                    total={
                      driverData?.driversTotals.find(
                        driver =>
                          driver.STATUS === DriverStatus.PENDING_APPROVAL,
                      )?.COUNT ?? 0
                    }
                    isMobile={isMobile}
                  />
                  <Card
                    title={'Registro incompleto'}
                    total={
                      driverData?.driversTotals.find(
                        driver =>
                          driver.STATUS ===
                          DriverStatus.INCOMPLETE_REGISTRATION,
                      )?.COUNT ?? 0
                    }
                    isMobile={isMobile}
                  />
                </Grid>
              </Box>
            </>
          ) : (
            <Box display="flex" flexWrap="wrap" justifyContent="space-between">
              <Grid container spacing={3}>
                <Card
                  title={'Tempo médio de aceite'}
                  total={formatTimeFromSeconds(
                    data?.FreightAverageAcceptTime ?? 0,
                  )}
                  icon="clock"
                  isMobile={isMobile}
                />
                <Card
                  title={'Aguardando coleta'}
                  total={Number(data?.AWAITING_DRIVER_ASSIGNMENT ?? 0)}
                  icon="hourglass"
                  isMobile={isMobile}
                />
                <Card
                  title={'Concluído'}
                  total={Number(data?.COMPLETED ?? 0)}
                  icon="box"
                  isMobile={isMobile}
                />
                <Card
                  title={'Com problema'}
                  total={Number(data?.PROBLEM_REPORTED ?? 0)}
                  icon="alert"
                  isMobile={isMobile}
                />
              </Grid>
            </Box>
          )}
        </>
      )}
    </>
  );
};

interface CardProps {
  title: string;
  total: number | string;
  icon?: any;
  isMobile: boolean;
}

function Card({ title, total, icon, isMobile }: CardProps) {
  const classes = useStyles();
  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Box width="100%">
        <MuiCard className={classes.card}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-start">
                  <img src={CARD_ICON[icon]} alt={icon} className="img-icons" />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-start">
                  <Typography
                    className={classes.title}
                    variant={isMobile ? 'h5' : 'h3'}
                    component="h2"
                  >
                    {total}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-start">
                  <Typography
                    className={classes.paragraph}
                    variant={isMobile ? 'body2' : 'body1'}
                  >
                    {title}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </MuiCard>
      </Box>
    </Grid>
  );
}

export default Cards;
